import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { match } from 'ts-pattern'
import FluentPrompt24Regular from '~icons/fluent/prompt-24-regular'
import RiVideoAiLine from '~icons/ri/video-ai-line'
import SolarCupStarLinear from '~icons/solar/cup-star-linear'
import ArtworkGrid from 'src/components/ArtworkList/ArtworkGrid'
import StatefulArtworkGrid from 'src/components/ArtworkList/StatefulArtworkGrid'
import {
  FeedCategory,
  type ArtworkFeedResult,
  type FeedCategoryType,
  useDailyRankingArtworkFeed,
  useFollowingNewsArtworkFeed,
  useListArtworkFeed,
  useNewestArtworkFeed,
  useRecommendedArtworkFeed,
  useTrendingArtworkFeed,
} from 'src/components/ArtworkList/useArtworkFeed'
import { HomeBannerCarousel } from 'src/components/BannerCarousel'
import SimpleGenerationForm from 'src/components/GenerationTask/SimpleGenerationForm'
import MetaTags from 'src/components/MetaTags'
import OrderSelect, {
  defaultChoicesForHome,
  useOrderState,
} from 'src/components/OrderSelect'
import TagDescription from 'src/components/Tag/TagDescription'
import TagGallery from 'src/components/Tag/TagGallery'
import { useTagInfo } from 'src/components/Tag/useTagInfo'
import { createTagPageUrl } from 'src/components/Tag/utils'
import { feedToolbarButtonStyles } from 'src/components/ui/FeedToolbarButton'
import { UILink } from 'src/components/ui/Link'
import Text from 'src/components/ui/Text'
import { useHomeInputboxRefreshExperiment } from 'src/experiments/experiments/2604/homeInputboxRefresh'
import { useSeoHeaderH1H2Experiment } from 'src/experiments/experiments/2604/seoHeaderH1H2'
import { Experiments } from 'src/experiments/experiments/constants'
import { ArtworkListPageContext } from 'src/pages/AppPage/ArtworkListPageContext'
import type {
  ArtworkWithMangaPreviewFragment,
  PageInfo,
} from 'src/services/apollo/generated/graphql'
import {
  OrderOption,
  translateOrderOptionForArtworkListQuery,
} from 'src/services/helpers/orderOptionParams'
import { useMe } from 'src/services/helpers/user'
import { useRefreshCount } from 'src/stores/signal'
import {
  ArtworkTypeFilterValue,
  getArtworkQueryArgsByArtworkFilter,
} from 'src/utils/artwork'
import type { Connection } from 'src/utils/graphql'
import { getNodesFromConnection } from 'src/utils/graphql'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { useHydratedExperimentVariant } from 'src/utils/hydrated-experiment'
import { formatQs, useQs } from 'src/utils/qs'
import {
  useArtworkListTagQueryDescription,
  useArtworkListTagQueryTitle,
} from 'src/utils/seo'
import { track, trackImageForRec } from 'src/utils/tracker'

/**
 * Loader-injected first-page artwork connection. Same shape produced by
 * `fetchListArtworksForSSR` / `fetchRecommendVisitorConnectionForSSR` in
 * the home + tag routes.
 */
type SSRInitialConnectionData = Connection<ArtworkWithMangaPreviewFragment> & {
  pageInfo?: PageInfo
  feedUuid?: string
}

export interface ArtworkListPageSSRPreviewProps {
  title: string
  description: string
  activeTagDisplayName?: string
  tagDescription?: string
  activeOrderLabel: string
  activeFilters: string[]
  initialArtworks: ArtworkWithMangaPreviewFragment[]
}

export function ArtworkListPageSSRPreview(
  props: ArtworkListPageSSRPreviewProps,
) {
  return (
    <ArtworkListPageContext.Provider value={{ insertCampaignAdUnit: false }}>
      <div className="flex flex-col gap-4 py-4 msm:py-6 mlg:py-8">
        <header className="flex max-w-3xl flex-col gap-3">
          <h1 className="text-2xl font-semibold mmd:text-3xl">{props.title}</h1>
          <p className="text-sm text-primary/80 mmd:text-base">
            {props.description}
          </p>
          <div className="mb-1 flex flex-wrap gap-2 text-xs">
            {props.activeTagDisplayName ? (
              <span className="inline-flex rounded-full border border-black/10 px-3 py-1 dark:border-white/10">
                #{props.activeTagDisplayName}
              </span>
            ) : null}
            <span className="inline-flex rounded-full bg-background-secondary px-3 py-1">
              {props.activeOrderLabel}
            </span>
            {props.activeFilters.map(filter => (
              <span
                key={filter}
                className="inline-flex rounded-full bg-background-secondary px-3 py-1"
              >
                {filter}
              </span>
            ))}
          </div>
        </header>

        {props.activeTagDisplayName && props.tagDescription ? (
          <TagDescription
            tagInfo={{
              name: props.activeTagDisplayName,
              description: props.tagDescription,
            }}
            className="flex-none w-full"
          />
        ) : null}

        <ArtworkGrid
          artworks={props.initialArtworks}
          loading={false}
          enableTracking={false}
          showDislike={false}
          showDislikeOverlay={false}
        />
      </div>
    </ArtworkListPageContext.Provider>
  )
}

const ArtworkListPage = (props: {
  /**
   * SSR loader injects the first-page artwork connection here.
   * We adapt it to main's new `initialData?: Pick<ArtworkFeedResult,
   * 'artworks' | 'feedUuid'>` shape inside the inner `ArtworkListGrid`
   * so the grid is populated on the first paint without waiting on the
   * client-side feed hooks.
   */
  ssrInitialConnectionData?: SSRInitialConnectionData
}) => {
  const { t } = useTranslation(['common', 'artwork', 'manga'])
  const [order, setOrder] = useOrderState(defaultChoicesForHome)
  const isMobile = useIsMobile()
  const enableOneLineLayout = isMobile

  const [{ tag, aid, type: filterType, hidePrompts }, setQs] = useQs()

  const tagInfo = useTagInfo(tag)
  const seoTitle = useArtworkListTagQueryTitle({ tag, tagInfo })
  const seoDescription = useArtworkListTagQueryDescription({ tag, tagInfo })

  const refreshTime = useRefreshCount()

  const { me, loading: meLoading } = useMe()
  const { value: hideEventVariant } = useHydratedExperimentVariant<
    'control' | 'treatment'
  >(Experiments.HideEventBeforeLoginAb, 'control')

  const showEvent = hideEventVariant === 'control' || !!me

  const { isTreatment: isSeoHeaderTreatment } = useSeoHeaderH1H2Experiment()
  const { isTreatment: isInputboxRefreshTreatment } =
    useHomeInputboxRefreshExperiment()

  const key = useMemo(() => {
    const v = {
      tag,
      order,
      type: filterType,
      hidePrompts,
      refreshTime,
    }
    return JSON.stringify(v)
  }, [tag, order, filterType, hidePrompts, refreshTime])

  const orderChoices: {
    label: ReactNode
    value: string
  }[] = [
    {
      label: t('common:order-option.recommend'),
      value: OrderOption.recommended,
    },
    {
      label: t('common:order-option.random'),
      value: OrderOption.trending,
    },
    {
      label: t('common:order-option.daily-ranking'),
      value: OrderOption.dailyRanking,
    },
    {
      label: t('common:order-option.following-news'),
      value: OrderOption.followingNews,
    },
    {
      label: t('common:order-option.newest'),
      value: OrderOption.newest,
    },
  ]

  const ctxValue = useMemo((): ArtworkListPageContext => {
    const disableCampaignAdUnitForSSR =
      typeof window === 'undefined' && Boolean(props.ssrInitialConnectionData)

    return {
      selectedContestTag: tagInfo.selectedContestTag,
      insertCampaignAdUnit: disableCampaignAdUnitForSSR ? false : showEvent,
    }
  }, [props.ssrInitialConnectionData, tagInfo, showEvent])

  // Looks like a tag, but actually isn't
  const animatedFilterTag = useMemo(
    () => ({
      icon: <RiVideoAiLine />,
      displayName: t('common:ranking.animated.label'),
      name: ArtworkTypeFilterValue.Animated,
      onClick: () => {
        track('order_click', {
          value: ArtworkTypeFilterValue.Animated,
          scene: 'home',
        })
        const isSelectingAnimated =
          filterType !== ArtworkTypeFilterValue.Animated
        const nextType = isSelectingAnimated
          ? ArtworkTypeFilterValue.Animated
          : ArtworkTypeFilterValue.All

        const shouldForceTrending =
          isSelectingAnimated && order === OrderOption.recommended

        setQs(prev => ({
          ...prev,
          sort: shouldForceTrending ? OrderOption.trending : prev.sort,
          type: nextType,
        }))
      },
      selected: filterType === ArtworkTypeFilterValue.Animated,
    }),
    [filterType, order, t, setQs],
  )

  const publicPromptFilterTag = useMemo(
    () => ({
      icon: <FluentPrompt24Regular />,
      displayName: t('common:ranking.public-prompts'),
      name: 'public-prompts' as ArtworkTypeFilterValue,
      onClick: () => {
        track('order_click', {
          value: 'visible_prompts',
          scene: 'home',
        })
        const isEnablingVisiblePrompts = hidePrompts !== 'false'
        const nextHidePrompts = isEnablingVisiblePrompts ? 'false' : null

        const shouldForceTrending =
          isEnablingVisiblePrompts && order === OrderOption.recommended

        setQs(prev => ({
          ...prev,
          sort: shouldForceTrending ? OrderOption.trending : prev.sort,
          hidePrompts: nextHidePrompts,
        }))
      },
      selected: hidePrompts === 'false',
    }),
    [hidePrompts, order, t, setQs],
  )

  const prependTagItems = useMemo(() => {
    return [animatedFilterTag, publicPromptFilterTag]
  }, [animatedFilterTag, publicPromptFilterTag])

  const orderSelectElement = (
    <OrderSelect
      choices={orderChoices}
      alwaysCollapse
      value={order}
      onChange={v => {
        track('order_click', { value: v, scene: 'home' })
        // Switching sort filter equals viewing a new feed
        trackImageForRec(
          'reco_browser_path_switch',
          {
            fromFeed: order,
            toFeed: v,
          },
          { regenFeedUuid: true },
        )
        return setOrder(v)
      }}
    />
  )

  const contestLinkElement = (
    <UILink
      href="/contest"
      className={feedToolbarButtonStyles({
        class: 'px-4',
      })}
      labelClassName="flex items-center gap-1.5"
    >
      <SolarCupStarLinear className="size-5 shrink-0" />
      <span className="text-sm font-normal">
        <Text i18nKey="common:tab.contest" />
      </span>
    </UILink>
  )

  const mobilePrependElements = enableOneLineLayout ? (
    <>
      {orderSelectElement}
      {contestLinkElement}
    </>
  ) : undefined

  if (aid != null) {
    return <Navigate to={`/artwork/${aid}/relevant`} />
  }

  const content = (
    <div>
      {isSeoHeaderTreatment && !me && !meLoading && (
        <div className="pt-8 msm:pt-12 pb-2 text-center">
          {/*
            Reserve final height so the block doesn't reflow when the lazy-
            loaded `common` namespace arrives. Three stages are needed
            because the type scale changes at msm (=600px) but the line-wrap
            point is at mmd (=900px) — at 600–900px the h1 is already
            text-4xl yet still wraps to two lines, so a single 1-line
            reservation there would under-reserve and CLS:
              <600px  (text-2xl / text-sm, 2 lines): 4rem    / 2.5rem
              msm:    (text-4xl / text-lg, 2 lines): 5rem    / 3.5rem
              mmd:    (text-4xl / text-lg, 1 line):  2.5rem  / 1.75rem
            Each rem matches Tailwind's default line-height for the active
            text size (text-2xl=2rem, text-4xl=2.5rem, text-sm=1.25rem,
            text-lg=1.75rem). Plain rem instead of `lh` because the lh unit
            isn't covered by our browserslist target.
          */}
          <h1 className="text-2xl msm:text-4xl font-bold text-black dark:text-white min-h-[4rem] msm:min-h-[5rem] mmd:min-h-[2.5rem]">
            {t('common:home.seo-h1')}
          </h1>
          <h2 className="mt-2 text-sm msm:text-lg font-normal text-gray-500 min-h-[2.5rem] msm:min-h-[3.5rem] mmd:min-h-[1.75rem]">
            <Trans
              t={t}
              i18nKey="common:home.seo-h2"
              components={{ strong: <strong className="font-semibold" /> }}
            />
          </h2>
        </div>
      )}
      {!isInputboxRefreshTreatment && showEvent && (
        <HomeBannerCarousel
          containerClassName="mt-8 mx-auto"
          containerStyle={{ maxWidth: 900 }}
          className="max-w-screen-mmd mx-auto"
        />
      )}
      <div className="py-4 msm:py-8">
        <SimpleGenerationForm
          isInputboxRefreshTreatment={isInputboxRefreshTreatment}
        />
      </div>
      {isInputboxRefreshTreatment && showEvent && (
        <HomeBannerCarousel
          containerClassName="mx-auto mb-4"
          containerStyle={{ maxWidth: 900 }}
          className="max-w-screen-mmd mx-auto"
        />
      )}
      <div className="mb-4 flex flex-wrap gap-2">
        {!enableOneLineLayout && (
          <>
            {orderSelectElement}
            {contestLinkElement}
          </>
        )}
        <TagGallery
          scene="home"
          navigateToTag={(identifier: string) =>
            createTagPageUrl('artwork', identifier)
          }
          navigateToContest={({ id }: { id: string }) => `/contest/${id}`}
          className={
            enableOneLineLayout
              ? 'w-full'
              : 'mmd:flex-1 max-mmd:w-full mmd:pl-2 overflow-hidden'
          }
          prependTagAlikeItems={prependTagItems}
          prependElements={mobilePrependElements}
        />
        {tag && (
          <TagDescription tagInfo={tagInfo} className="flex-none w-full" />
        )}
      </div>
      {/*
        Previously wrapped in `<Suspense>` as a safety net for
        suspending hooks below. With `useSuspense: false` in our
        i18next config and no `useSuspenseQuery` / `React.lazy` /
        `dynamic({ ssr: false })` in the grid render path, the
        boundary catches nothing — but React 19's streaming
        Suspense protocol still emits the placeholder + hidden
        swap target + swap script the moment **anything** in the
        subtree throws server-side, which leaves no-JS / NoScript
        viewers of `/zh` looking at a blank artwork grid (real
        content hidden in `<div id="S:0">`, swap script never
        runs). Removing the boundary lets the grid HTML inline
        into `<main>` so SSR is self-rendering again.
      */}
      <ArtworkListGrid
        key={key}
        order={order}
        tag={tag}
        filterType={filterType}
        hidePrompts={hidePrompts}
        ssrInitialConnectionData={props.ssrInitialConnectionData}
      />
    </div>
  )

  return (
    <ArtworkListPageContext.Provider value={ctxValue}>
      <MetaTags
        title={seoTitle}
        titleOptions={{
          suffix: t('common:meta.title.home-suffix'),
        }}
        description={seoDescription}
        ogUrlPathname={formatQs({ tag }) ? `/?${formatQs({ tag })}` : ''}
      />
      {content}
    </ArtworkListPageContext.Provider>
  )
}

function ArtworkListGrid({
  order,
  tag,
  filterType,
  hidePrompts,
  ssrInitialConnectionData,
}: {
  order: string
  tag: string | undefined
  filterType: string | undefined
  hidePrompts: string | undefined
  ssrInitialConnectionData?: SSRInitialConnectionData
}) {
  // Adapt SSR loader's connection-shaped seed to main's `ArtworkFeedResult`
  // so the inner `useMemo` can keep its existing logic without caring how
  // the seed was produced.
  const initialData = useMemo<
    Pick<ArtworkFeedResult, 'artworks' | 'feedUuid'> | undefined
  >(() => {
    if (!ssrInitialConnectionData) return undefined
    const artworks = getNodesFromConnection(
      ssrInitialConnectionData,
    ) as ArtworkFeedResult['artworks']
    if (artworks.length === 0) return undefined
    return {
      artworks,
      feedUuid: ssrInitialConnectionData.feedUuid,
    }
  }, [ssrInitialConnectionData])

  const artworkQueryArgs = getArtworkQueryArgsByArtworkFilter({
    type: (filterType as ArtworkTypeFilterValue) || ArtworkTypeFilterValue.All,
    hidePrompts: hidePrompts === 'false' ? false : undefined,
  })
  // Dedicated oRPC feeds (trending/newest/etc.) don't accept these filters,
  // so any of them forces the Default path through the GraphQL `listArtworks`
  // query, where the ES resolver applies the filter via `handleTerm`.
  const hasListFilters = !!(
    tag ||
    artworkQueryArgs.i2vPro ||
    artworkQueryArgs.extraArgs?.hidePrompts != null
  )
  const translatedOrder = translateOrderOptionForArtworkListQuery(
    order,
    hasListFilters,
  )
  const feedType = match({ order, hasListFilters })
    .returnType<FeedCategoryType>()
    .with(
      { order: OrderOption.recommended, hasListFilters: false },
      () => FeedCategory.Recommendation,
    )
    .with(
      { order: OrderOption.trending, hasListFilters: false },
      () => FeedCategory.Trending,
    )
    .with(
      { order: OrderOption.dailyRanking, hasListFilters: false },
      () => FeedCategory.DailyRanking,
    )
    .with(
      { order: OrderOption.followingNews, hasListFilters: false },
      () => FeedCategory.FollowingNews,
    )
    .with(
      { order: OrderOption.newest, hasListFilters: false },
      () => FeedCategory.Newest,
    )
    .otherwise(() => FeedCategory.Default)

  const listFeed = useListArtworkFeed(
    feedType === FeedCategory.Default
      ? { tag, order: translatedOrder, ...artworkQueryArgs }
      : null,
  )
  const recFeed = useRecommendedArtworkFeed(
    feedType === FeedCategory.Recommendation ? { first: 24 } : null,
  )
  const trendingFeed = useTrendingArtworkFeed(
    feedType === FeedCategory.Trending ? { first: 24 } : null,
  )
  const dailyRankingFeed = useDailyRankingArtworkFeed(
    feedType === FeedCategory.DailyRanking ? { first: 24 } : null,
  )
  const followingNewsFeed = useFollowingNewsArtworkFeed(
    feedType === FeedCategory.FollowingNews ? { first: 24 } : null,
  )
  const newestFeed = useNewestArtworkFeed(
    feedType === FeedCategory.Newest ? { first: 24 } : null,
  )
  const fetchedFeed = match(feedType)
    .with(FeedCategory.Recommendation, () => recFeed)
    .with(FeedCategory.Trending, () => trendingFeed)
    .with(FeedCategory.DailyRanking, () => dailyRankingFeed)
    .with(FeedCategory.FollowingNews, () => followingNewsFeed)
    .with(FeedCategory.Newest, () => newestFeed)
    .otherwise(() => listFeed)

  // While the real fetch is in-flight and has no data yet, show
  // `initialData` (the loader's `ssrInitialConnectionData` adapted
  // above) as a placeholder so the grid is populated immediately.
  //
  // Override `loading` to `false` in that branch as well: the
  // downstream `<BaseArtworkInfiniteScroll>` decides between
  // `renderLoading()` (skeleton cards) and `renderContent()` (real
  // cards) purely on `feedResult.loading`. Without this override the
  // SSR first-paint emits a screenful of skeletons even though we
  // already have real artwork data in `initialData.artworks` to
  // render — which on `/zh` (and any other home/list page) reads as
  // "the SSR HTML has no `<img>` list" for JS-disabled crawlers /
  // NoScript users, because the `<img>` tags live inside the real
  // cards (`renderContent`) and aren't emitted at all when the loading
  // branch fires.
  const feedResult = useMemo<ArtworkFeedResult>(
    () =>
      initialData && fetchedFeed.loading && fetchedFeed.artworks.length === 0
        ? {
            ...fetchedFeed,
            artworks: initialData.artworks,
            feedUuid: initialData.feedUuid,
            loading: false,
          }
        : fetchedFeed,
    [initialData, fetchedFeed],
  )

  return (
    <StatefulArtworkGrid
      feedResult={feedResult}
      order={translatedOrder}
      trackingArgs={{ scene: 'home', feed: order }}
      enableTracking
      showDislike
      showDislikeOverlay
    />
  )
}

export default ArtworkListPage
